import WordArray from 'crypto-js/lib-typedarrays';
import { reportSecurityEvent } from './monitoringUtils';
import { supabase, isDemoMode } from '../lib/supabase';

/**
 * Security utility functions for the application
 */

interface SecurityAuditLogEntry {
  eventType: string;
  userId: string;
  timestamp: string;
  ipAddress: string;
  userAgent: string;
   
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details: Record<string, any>;
  status: 'success' | 'failure' | 'warning';
}

function getBrowserUserAgent(): string {
  return typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
}

function hasBrowserStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function getCryptoApi(): Crypto | undefined {
  return typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
}

/**
 * Sync a security event to the backend security_events table (Supabase RPC)
 * This provides a unified audit trail for enterprise compliance
 */
const syncSecurityEventToBackend = async (
  eventName: string,
  outcome: 'success' | 'failure' | 'warning' | 'blocked' | 'no_change',
   
  targetId?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>,
  targetType?: string
): Promise<void> => {
  // Skip backend sync in demo mode
  if (isDemoMode || !supabase) {
    return;
  }

  try {
    const { error } = await supabase.rpc('log_security_event', {
      p_event_name: eventName,
      p_outcome: outcome,
      p_target_id: targetId || null,
      p_metadata: metadata || {},
      p_target_type: targetType || null
    });

    if (error) {
      // Don't throw - backend logging is best-effort
      console.warn('Failed to sync security event to backend:', error.message);
    }
  } catch (err) {
    console.warn('Backend security event sync failed:', err);
  }
};

/**
 * Generate a secure random token
 * @param length - Length of the token to generate
 * @returns A secure random token
 */
export const generateSecureToken = (length: number = 32): string => {
  const byteLength = Number.isFinite(length) && length > 0 ? Math.floor(length) : 32;
  const cryptoApi = getCryptoApi();

  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(byteLength);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  return WordArray.random(byteLength).toString();
};

/**
 * Log a security event to the audit log
 * @param eventType - Type of security event
 * @param userId - User ID associated with the event
 * @param details - Additional details about the event
 * @param status - Status of the event (success, failure, etc.)
 */
export const logSecurityEvent = async (eventType: 'login' | 'logout' | 'password_change' | 'encryption_enabled' |
  'encryption_disabled' | 'key_rotation' | 'file_upload' | 'file_download' |
  'permission_change' | 'role_change' | 'user_creation' | 'user_deletion' |
  'user_update' | 'user_suspended' | 'user_reactivated' | 'membership_status_change' |
  'access_denied' | 'portal_access_denied' | 'suspicious_activity' | 'password_reset_request' |
  // Invitation system events (see INVITATION_EVENTS_SCHEMA.md)
  'invitation_created' | 'invitation_email_sent' | 'invitation_email_failed' |
  'invitation_preview_viewed' | 'invitation_lookup_failed' | 'invitation_accepted' |
  'invitation_accept_denied' | 'invitation_email_mismatch_blocked' | 'invitation_revoked' |
  'invitation_expired' | 'invitation_token_rotated' | 'break_glass_invite_override' |
  'security_events_cleanup' | 'explicit_right.assigned' | 'explicit_right.granted' |
  'audit_export' | 'security_claim_updated' |
  'report_generated' | 'report_submitted' | 'report_finalized' | 'report.delivery.denied' |
  // MFA events
  'mfa' |
  // Settings events
  'settings_updated' |
  // Recovery key events
  'recovery_keys_generated' | 'recovery_keys_exported' | 'recovery_key_used' |
  // Break glass events
   
  'break_glass_requested' | 'break_glass_completed' | 'break_glass_failed',
  userId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details: Record<string, any> = {},
  status: 'success' | 'failure' | 'warning' = 'success'
): Promise<void> => {
  try {
    const userAgent = getBrowserUserAgent();

    // Create audit log entry
    const logEntry = {
      eventType,
      userId,
      timestamp: new Date().toISOString(),
      ipAddress: 'unknown',
      userAgent,
      details,
      status
    };

    // Browser storage is a demo fixture only. Production activity is written
    // to the canonical server repository and is never reconstructed from the
    // current browser.
    if (isDemoMode && hasBrowserStorage()) {
      const auditLog = JSON.parse(localStorage.getItem('security_audit_log') || '[]');
      auditLog.push(logEntry);
      localStorage.setItem('security_audit_log', JSON.stringify(auditLog));
    }

    // Report security events to Sentry if they're not successful
    if (status !== 'success') {
      reportSecurityEvent(eventType, userId, details, status);
    }

    // AX1: Sync to unified backend security_events table
    // This provides a canonical audit trail for enterprise compliance
    await syncSecurityEventToBackend(
      eventType,
      status,
      userId,
      { ...details, userAgent, source: 'frontend' },
      'user'
    );
  } catch (error) {
    console.error('Failed to log security event:', error);
  }
};

/**
 * Get the security audit log
 * @param limit - Maximum number of entries to return
 * @param userId - Filter by user ID
 * @param eventType - Filter by event type
 * @returns The filtered audit log entries
 */
export const getSecurityAuditLog = (limit: number = 100,
  userId?: string,
  eventType?: string
): SecurityAuditLogEntry[] => {
  try {
    if (!isDemoMode) return [];
    if (!hasBrowserStorage()) return [];
    const auditLog = JSON.parse(localStorage.getItem('security_audit_log') || '[]');

    // Apply filters
    let filteredLog = auditLog;

    if (userId) {
      filteredLog = filteredLog.filter((entry: SecurityAuditLogEntry) => entry.userId === userId);
    }

    if (eventType) {
      filteredLog = filteredLog.filter((entry: SecurityAuditLogEntry) => entry.eventType === eventType);
    }

    // Sort by timestamp (newest first)
    filteredLog.sort((a: Record<string, unknown>, b: Record<string, unknown>) => new Date(b.timestamp as string).getTime() - new Date(a.timestamp as string).getTime());

    // Apply limit
    return filteredLog.slice(0, limit);
  } catch (error) {
    console.error('Failed to get security audit log:', error);
    return [];
  }
};

/**
 * Scan a file for malware
 * @param file - The file to scan
 * @returns Promise resolving to a scan result
 */
export const scanFileForMalware = async (file: File): Promise<{
  safe: boolean;
  threatType?: string;
  scanId?: string;
}> => {
  try {
    // Get file extension
    const extension = file.name.split('.').pop()?.toLowerCase();

    // List of potentially dangerous extensions
    const dangerousExtensions = ['exe', 'bat', 'cmd', 'sh', 'js', 'vbs', 'ps1', 'msi', 'dll'];

    // This is a deterministic client-side precheck, not a real malware scanner.
    if (dangerousExtensions.includes(extension || '')) {
      return {
        safe: false,
        threatType: 'blocked_executable_extension',
        scanId: generateSecureToken(8)
      };
    }

    return {
      safe: true,
      scanId: generateSecureToken(8)
    };
  } catch (error) {
    console.error('Malware scan failed:', error);

    // In case of scan failure, err on the side of caution
    return {
      safe: false,
      threatType: 'scan_failed'
    };
  }
};

/**
 * Validate file safety based on type and content
 * @param file - The file to validate
 * @returns Whether the file is considered safe
 */
export const validateFileSafety = async (file: File): Promise<boolean> => {
  // Check file size (max 50MB)
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  if (file.size > MAX_FILE_SIZE) {
    return false;
  }

  // Check file type
  const allowedTypes = [
    // Documents
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',

    // Images
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/svg+xml',
    'image/webp',

    // Archives
    'application/zip',
    'application/x-zip-compressed',

    // Other
    'application/json',
    'text/markdown'
  ];

  if (!allowedTypes.includes(file.type)) {
    return false;
  }

  // Scan for malware
  const scanResult = await scanFileForMalware(file);
  return scanResult.safe;
};
